import { Box, Button, keyframes, styled, Typography } from "@mui/material";
import type { RenderStateCamera } from "@novorender/api";
import { Error as ErrorIcon } from "@procore/core-icons";
import { vec2 } from "gl-matrix";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import {
    selectIsProcoreMobileDesign,
    selectMinimapTilingEnabled,
    selectProcoreContext,
    selectSceneId,
} from "slices/explorer";
import { AsyncStatus } from "types/misc";
import { log } from "utils/log";

import { downloadDrawingData } from "./downloadDrawingData";
import { LargeMapWeb } from "./largeMapWeb";
import {
    clearNavigationPin,
    type MiniMapEmptyStateSituation,
    selectCurrentDrawing,
    selectDrawingPlacements,
    selectIsMiniMapWidgetShown,
    selectMiniMapDrawings,
    selectMiniMapFetching,
    selectMiniMapLevels,
    selectMiniMapMappedSheetCount,
    selectMiniMapOnAddSheetsForNavigation,
    selectNavigationPin,
    setCurrentDrawing,
} from "./minimapSlice";
import {
    calculateImageFitBox,
    drawPositionIndicator,
    modelToDrawingSpace,
    resolveSheetForCamera,
} from "./miniMapUtils";
import { buildTilingRequest } from "./tiledDrawing";
import type { DrawingSpacePosition } from "./types";
import { useDrawingImage } from "./useDrawingImage";
import { useMiniMapData } from "./useMiniMapData";

const l = log.miniMap.miniMapWeb;

export const MINIMAP_WEB_ELEMENT_ID = "minimap-web-container";

const MiniMapContainer = styled(Box)({
    display: "flex",
    flexDirection: "column",
    gap: 10,
    alignItems: "flex-end",
    overflow: "visible",
    position: "absolute",
    bottom: 16,
    right: 16,
    pointerEvents: "auto",
});

const CanvasWrapper = styled(Box)({
    position: "relative",
    "&:hover .hover-layer": {
        borderColor: "rgba(255, 255, 255, 0.5)",
        boxShadow: "0px 0px 28px 0px rgba(0, 0, 0, 0.45)",
        opacity: 1,
    },
    "&:hover canvas": {
        boxShadow: "none",
    },
});

const HoverLayer = styled(Box)({
    position: "absolute",
    border: "4px solid transparent",
    borderRadius: 12,
    top: -3,
    right: -3,
    bottom: -3,
    left: -3,
    pointerEvents: "none",
    transition: "all 300ms ease-out",
    boxShadow: "0px 0px 0px 0px rgba(0, 0, 0, 0)",
    opacity: 1,
});

const StyledCanvas = styled("canvas", {
    shouldForwardProp: (prop) => prop !== "disabled",
})<{ disabled: boolean }>(({ disabled }) => ({
    display: "block",
    borderRadius: 8,
    boxShadow: "0px 0px 20px 0px rgba(0, 0, 0, 0.2)",
    transition: "box-shadow 300ms ease-out",
    backgroundColor: "white",
    cursor: disabled ? undefined : "pointer",
}));

const shimmerAnimation = keyframes`
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
`;

const ShimmerOverlay = styled(Box, {
    shouldForwardProp: (prop) => prop !== "visible",
})<{ visible: boolean }>(({ visible }) => ({
    position: "absolute",
    inset: 0,
    borderRadius: 8,
    overflow: "hidden",
    background: "linear-gradient(135deg, #a0a0a0, #d0d0d0)",
    opacity: visible ? 1 : 0,
    transition: "opacity 300ms ease-out",
    pointerEvents: "none",
    "&::after": {
        content: '""',
        position: "absolute",
        inset: 0,
        background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
        animation: visible ? `${shimmerAnimation} 1.5s infinite` : "none",
    },
}));

const ErrorOverlay = styled(Box, {
    shouldForwardProp: (prop) => prop !== "visible",
})<{ visible: boolean }>(({ visible }) => ({
    position: "absolute",
    inset: 0,
    borderRadius: 8,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    background: "white",
    opacity: visible ? 1 : 0,
    transition: "opacity 300ms ease-out",
    pointerEvents: "none",
}));

const EmptyStateWrapper = styled(Box)({
    position: "relative",
    width: 240,
    height: 170,
});

const EmptyStateBorder = styled(Box)({
    position: "absolute",
    border: "4px solid rgba(255, 255, 255, 0.5)",
    borderRadius: 12,
    top: -3,
    right: -3,
    bottom: -3,
    left: -3,
    pointerEvents: "none",
    boxShadow: "0px 0px 28px 0px rgba(0, 0, 0, 0.28)",
});

const EmptyStateCard = styled(Box)({
    width: 240,
    height: 170,
    borderRadius: 8,
    background: "rgba(255, 255, 255, 1)",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "space-between",
    padding: "14px",
    boxSizing: "border-box",
    fontFamily: "Inter, sans-serif",
});

const EmptyStateTextBlock = styled(Box)({
    width: 212,
    display: "flex",
    flexDirection: "column",
    gap: 8,
});

const EmptyStateTitle = styled(Typography)({
    width: 212,
    height: 24,
    margin: 0,
    fontFamily: "Inter, sans-serif",
    fontWeight: 600,
    fontSize: 16,
    lineHeight: "24px",
    letterSpacing: "0.15px",
    color: "rgba(35, 39, 41, 1)",
});

const EmptyStateDescription = styled(Typography)({
    width: 212,
    height: 40,
    margin: 0,
    fontFamily: "Inter, sans-serif",
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "20px",
    letterSpacing: "0.15px",
    color: "rgba(106, 118, 124, 1)",
});

const EmptyStateButton = styled(Button)({
    width: 153,
    height: 36,
    minWidth: 153,
    padding: "2px 6px",
    textTransform: "none",
    pointerEvents: "auto",
    backgroundColor: "rgba(232, 236, 237, 1)",
    color: "rgba(35, 39, 41, 1)",
    fontFamily: "Inter, sans-serif",
    fontWeight: 600,
    fontSize: 14,
    lineHeight: "20px",
    letterSpacing: "0.15px",
    borderRadius: 4,
    boxShadow: "none",
    "&:hover": {
        backgroundColor: "rgba(220, 225, 227, 1)",
        boxShadow: "none",
    },
});

// `onAddSheets` is required — the null guard lives in `MiniMapWeb`
// (`if (!onAddSheetsForNavigation) return null`) so this component is never
// rendered without a handler.  The `situation` distinction ("no-mapped" vs
// "not-used-for-nav") is host-facing only: both cases show identical copy here
// but the value is forwarded to the host callback so it can act differently.
function MiniMapEmptyState({ onAddSheets }: { onAddSheets: () => void }) {
    const { t } = useTranslation();

    return (
        <EmptyStateWrapper>
            <EmptyStateBorder />
            <EmptyStateCard>
                <EmptyStateTextBlock>
                    <EmptyStateTitle>{t("miniMapEmptyTitle")}</EmptyStateTitle>
                    <EmptyStateDescription>{t("miniMapEmptyDescription")}</EmptyStateDescription>
                </EmptyStateTextBlock>
                <EmptyStateButton variant="contained" disableElevation onClick={onAddSheets}>
                    {t("miniMapAddSheets")}
                </EmptyStateButton>
            </EmptyStateCard>
        </EmptyStateWrapper>
    );
}

interface MiniMapProps {
    className?: string;
}

// Longest-side cap for the mini map's decoded bitmap. The canvas is 240x172, so
// this comfortably covers high-DPR screens while keeping the decoded copy at a
// couple of MB instead of ~100MB.
const MINI_MAP_MAX_IMAGE_SIZE = 768;

function MiniMap({ onClick, largeMapOpen }: { onClick?: () => void; largeMapOpen: boolean }) {
    const { t } = useTranslation();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const currentDrawing = useAppSelector(selectCurrentDrawing);
    const levels = useAppSelector(selectMiniMapLevels);
    const drawings = useAppSelector(selectMiniMapDrawings);
    const placements = useAppSelector(selectDrawingPlacements);
    const navigationPin = useAppSelector(selectNavigationPin);
    const tilingEnabled = useAppSelector(selectMinimapTilingEnabled);
    const isMobileDesign = useAppSelector(selectIsProcoreMobileDesign);
    const procoreContext = useAppSelector(selectProcoreContext);
    const sceneId = useAppSelector(selectSceneId);
    const dispatch = useAppDispatch();
    // The web mini map fits the whole drawing into a ~240x172 canvas, so a
    // downscaled bitmap is visually identical while keeping only a tiny decoded
    // copy resident. While the large map is open it fully covers the mini map, so
    // release the bitmap (pass null) — only the large map's full-resolution
    // decode is resident then, never both. It re-decodes (cheaply) on close.
    const img = useDrawingImage(largeMapOpen ? null : currentDrawing, MINI_MAP_MAX_IMAGE_SIZE);
    const {
        state: { view, internalEventEmitter },
    } = useExplorerGlobals();

    const [drawingPosition, setDrawingPosition] = useState<DrawingSpacePosition>({
        position: vec2.fromValues(0, 0),
        rotation: 0,
    });

    const canvasWidth = 240;
    const canvasHeight = 172;

    const drawMap = useCallback(
        (
            ctx: CanvasRenderingContext2D,
            img: ImageBitmap,
            canvasWidth: number,
            canvasHeight: number,
            drawingPosition: DrawingSpacePosition,
        ) => {
            // A detached (closed) ImageBitmap reports width 0. A draw driven by
            // a closure/animation frame captured before `useDrawingImage`
            // released the bitmap can receive a stale, detached image; drawing
            // it throws "image source is detached".
            if (img.width === 0) {
                return;
            }
            ctx.clearRect(0, 0, canvasWidth, canvasHeight);
            const { width, height, x, y } = calculateImageFitBox({
                imageDimensions: [img.width, img.height],
                canvasDimensions: [canvasWidth, canvasHeight],
            });
            const positionX = drawingPosition.position[0] * width + x;
            const positionY = drawingPosition.position[1] * height + y;
            const rotation = drawingPosition.rotation;
            ctx.drawImage(img, x, y, width, height);
            drawPositionIndicator(ctx, positionX, positionY, rotation);
        },
        [],
    );

    useEffect(() => {
        if (!view || !internalEventEmitter || levels.length === 0) {
            return;
        }

        const handleCameraUpdated = (camera: RenderStateCamera) => {
            const { drawingId: targetDrawingId, releasePin } = resolveSheetForCamera({
                camera,
                levels,
                placements,
                currentDrawingId: currentDrawing?.id,
                navigationPin,
            });

            if (releasePin) {
                dispatch(clearNavigationPin());
            }

            if (targetDrawingId && targetDrawingId !== currentDrawing?.id) {
                l.debug(`Updating drawing to match camera position. From ${currentDrawing?.id} to ${targetDrawingId}`);
                dispatch(setCurrentDrawing(targetDrawingId));

                // Fetch the drawing data for the new drawing if we don't have it already
                const targetDrawing = drawings[targetDrawingId];
                if (!targetDrawing) {
                    l.error("Target drawing not found in state for id:", targetDrawingId);
                } else {
                    const { status } = targetDrawing.drawingData.previewAsyncState;
                    if (status === AsyncStatus.Initial) {
                        l.debug("Fetching drawing data for new drawing:", targetDrawing);
                        downloadDrawingData({
                            dispatch,
                            drawingId: targetDrawing.id,
                            url: targetDrawing.drawingData.url,
                            telemetrySource: "miniMap.miniMapWeb.fetchDrawingData",
                            tiling: buildTilingRequest(
                                tilingEnabled,
                                procoreContext,
                                sceneId,
                                targetDrawing.drawingData,
                                isMobileDesign,
                            ),
                        }).then(() => {
                            l.debug("Finished downloading drawing data for mini map:", targetDrawing.id);
                        });
                    } else {
                        l.debug("Drawing data for target drawing already in state, no need to fetch:", targetDrawing);
                    }
                }
            }

            if (currentDrawing?.modelAB && currentDrawing?.drawingAB) {
                const result = modelToDrawingSpace(currentDrawing.modelAB, currentDrawing.drawingAB, camera);
                setDrawingPosition(result);
            }
        };

        handleCameraUpdated(view.renderState.camera);

        const unsubscribe = internalEventEmitter.on("cameraUpdated", handleCameraUpdated);

        return () => {
            unsubscribe();
        };
    }, [
        view,
        internalEventEmitter,
        levels,
        placements,
        navigationPin,
        currentDrawing,
        dispatch,
        drawings,
        tilingEnabled,
        isMobileDesign,
        procoreContext,
        sceneId,
    ]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }

        const ctx = canvas.getContext("2d");
        if (!ctx) {
            return;
        }

        const dpr = window.devicePixelRatio || 1;

        canvas.width = canvasWidth * dpr;
        canvas.height = canvasHeight * dpr;
        canvas.style.width = `${canvasWidth}px`;
        canvas.style.height = `${canvasHeight}px`;
        ctx.scale(dpr, dpr);

        if (!currentDrawing) {
            l.debug("No current drawing selected for mini map, drawing placeholder");
            drawBackground(ctx, canvasWidth, canvasHeight);
            return;
        }

        // The mini map only consumes the preview, so its render is driven by the
        // preview state, not the (large-map-only) tiles state.
        const { status } = currentDrawing.drawingData.previewAsyncState;
        l.debug(
            "Drawing mini map, currentDrawing:",
            currentDrawing.id,
            "preview state:",
            currentDrawing.drawingData.previewAsyncState,
        );
        switch (status) {
            case AsyncStatus.Initial:
            case AsyncStatus.Loading:
                drawBackground(ctx, canvasWidth, canvasHeight);
                break;
            case AsyncStatus.Success:
                if (img) {
                    drawMap(ctx, img, canvasWidth, canvasHeight, drawingPosition);
                } else {
                    // Preview downloaded but not decoded yet; show placeholder
                    // until `useDrawingImage` resolves the bitmap.
                    drawBackground(ctx, canvasWidth, canvasHeight);
                }
                break;
            case AsyncStatus.Error:
            default:
                drawBackground(ctx, canvasWidth, canvasHeight);
        }
    }, [currentDrawing, drawingPosition, canvasWidth, canvasHeight, drawMap, img]);

    const status = currentDrawing?.drawingData.previewAsyncState.status;
    const isLoading = !currentDrawing || status === AsyncStatus.Initial || status === AsyncStatus.Loading;
    const isError = status === AsyncStatus.Error;
    const disabled = status !== AsyncStatus.Success;

    return (
        <Box
            className="minimap-inner"
            sx={{
                position: "relative",
                width: canvasWidth,
                height: canvasHeight,
            }}
        >
            <StyledCanvas ref={canvasRef} onClick={disabled ? undefined : onClick} disabled={disabled} />
            <ShimmerOverlay visible={isLoading} role="status" aria-live="polite" aria-label={t("loadingDrawing")} />
            <ErrorOverlay visible={isError} role="alert" aria-live="polite">
                <Box aria-hidden="true" sx={{ display: "flex" }}>
                    <ErrorIcon size="lg" />
                </Box>
                <Typography variant="body2">{t("loadingDrawingError")}</Typography>
            </ErrorOverlay>
        </Box>
    );
}

function drawBackground(ctx: CanvasRenderingContext2D, width: number, height: number) {
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, "#a0a0a0");
    gradient.addColorStop(1, "#d0d0d0");
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
}

export function MiniMapWeb({ className }: MiniMapProps) {
    const [isSheetOpen, setIsSheetOpen] = useState(false);

    const handleMiniMapClick = () => {
        setIsSheetOpen(true);
    };

    const handleSheetClose = () => {
        setIsSheetOpen(false);
    };

    // Called for its subscription and for the effect that mirrors its
    // loading/error predicates into the slice. This component deliberately
    // renders off those mirrored values rather than the hook's return, so that
    // it, the Settings toggle and `useViewportSafeArea` are all driven by the
    // same store state and cannot disagree about what is on screen.
    useMiniMapData();

    const currentDrawing = useAppSelector(selectCurrentDrawing);
    const levels = useAppSelector(selectMiniMapLevels);
    const mappedSheetCount = useAppSelector(selectMiniMapMappedSheetCount);
    const onAddSheetsForNavigation = useAppSelector(selectMiniMapOnAddSheetsForNavigation);
    const isMiniMapDataLoading = useAppSelector(selectMiniMapFetching);
    // Single source of truth shared with Settings and `useViewportSafeArea`.
    // Covers the terminal-error case and the missing-host-handler case; which of
    // the two views to render is decided below.
    const isWidgetShown = useAppSelector((state) => selectIsMiniMapWidgetShown(state, false));

    const hasNavSheets = levels.length > 0;
    if (!isWidgetShown) {
        return null;
    }

    if (isMiniMapDataLoading || hasNavSheets) {
        return (
            <>
                <MiniMapContainer id={MINIMAP_WEB_ELEMENT_ID} className={className}>
                    <CanvasWrapper>
                        <HoverLayer className="hover-layer" />
                        <MiniMap onClick={currentDrawing ? handleMiniMapClick : undefined} largeMapOpen={isSheetOpen} />
                    </CanvasWrapper>
                </MiniMapContainer>

                {currentDrawing !== null && <LargeMapWeb open={isSheetOpen} onClose={handleSheetClose} />}
            </>
        );
    }

    if (!onAddSheetsForNavigation) {
        return null;
    }

    const situation: MiniMapEmptyStateSituation = mappedSheetCount === 0 ? "no-mapped" : "not-used-for-nav";

    return (
        <MiniMapContainer id={MINIMAP_WEB_ELEMENT_ID} className={className}>
            <MiniMapEmptyState onAddSheets={() => onAddSheetsForNavigation(situation)} />
        </MiniMapContainer>
    );
}
